import { taskMax, taskScore } from '../../domain/scoring';
import { toNumber } from '../../domain/points';
import { usePoints, useT } from '../../i18n';
import type { Task } from '../../domain/types';
import { selectAssessment, useAppStore } from '../../store/useAppStore';
import { Dialog, DialogFooterButton } from '../../ui/Dialog';
import styles from './Sheets.module.css';
import { HtmlText } from '../../ui/HtmlText';
import { punkteRaster } from './punkteRaster';

/**
 * Punktzahl einer Aufgabe von Hand setzen. Bei globalen Aufgaben ist das der einzige Weg zur
 * Punktzahl, bei den übrigen ein Eingriff, der die Rechnung übersteuert.
 */
export function ScoreDialog({ task }: { task: Task }) {
  const t = useT();
  const points = usePoints();
  const assessment = useAppStore(selectAssessment);
  const halfPoints = useAppStore((s) => s.halfPoints);
  const setOverride = useAppStore((s) => s.setOverride);
  const closeSheet = useAppStore((s) => s.closeSheet);

  const max = toNumber(taskMax(task));
  const current = toNumber(taskScore(task, assessment));
  const overridden = assessment.override[task.id] != null;

  // Die Schrittweite der Aufgabe (Relay `pointsStep`) geht vor; ohne sie gilt die Einstellung
  // halbe/ganze Punkte (Entscheidung des Nutzers, 2026-10-09).
  const spalten = punkteRaster(max, task.pointsStep ?? (halfPoints ? 0.5 : 1));

  const cell = (value: number) => (
    <button
      key={value}
      type="button"
      className={`tap ${styles.scoreCell}`}
      data-on={Math.abs(current - value) < 1e-9}
      onClick={() => setOverride(task.id, value)}
    >
      {points(value)}
    </button>
  );

  return (
    <Dialog
      kicker={[task.typeLabel, t.score.upTo(points(max))].filter(Boolean).join(' · ')}
      title={<HtmlText text={task.title} />}
      onClose={closeSheet}
    >
      <div className={`sc ${styles.scoreGrid}`}>
        <div className={styles.scoreColumns}>
          {spalten.map((werte) => (
            <div key={werte[0]} className={styles.scoreColumn}>
              {werte.map(cell)}
            </div>
          ))}
        </div>
      </div>

      {overridden && task.scoring.kind !== 'global' && (
        <DialogFooterButton onClick={() => setOverride(task.id, null)}>
          {t.score.undoOverride}
        </DialogFooterButton>
      )}
    </Dialog>
  );
}
